import React, { useState } from 'react';
import PageMeta from '../components/PageMeta';
import { useNavigate } from 'react-router-dom';
import {
  ArrowRight, CheckCircle2, Star, Users, Award, Globe, TrendingUp, Target,
  Zap, BookOpen, Clock, Calendar, Phone, Mail, ChevronDown, Sparkles,
  Shield, Rocket, GraduationCap, Brain, Lightbulb, Loader2,
  Gamepad2, Cpu, Monitor, Joystick, Swords, Box
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { trackWhatsAppClick, trackFormSubmission } from '../utils/analytics';
import CertificatesSection from '../components/CertificatesSection';
import AdmissionEnrollment from '../components/AdmissionEnrollment';
import EnquireNowButton from '../components/EnquireNowButton';
import HeroPricingCard from '../components/HeroPricingCard';
import BrandLogo from '../components/BrandLogo';
import SiteHeader from '../components/SiteHeader';
import SiteFooter from '../components/SiteFooter';
import MobileCTABar from '../components/MobileCTABar';

const GameDevelopmentCoursePage = () => {
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState(null);
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/leads`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: formData.name, email: formData.email, phone: formData.phone, message: formData.message, course: 'Game Development Course (4 Months)', source: 'game-dev-page' })
      });
      if (res.ok) { trackFormSubmission('game_dev_application', { course: 'Game Development' }); navigate('/thank-you'); }
    } catch (err) { console.error(err); }
    setSubmitting(false);
  };

  const [payForm, setPayForm] = useState({ payName: '', payEmail: '', payPhone: '' });
  const [paySubmitting, setPaySubmitting] = useState(false);

  const handleAdmissionPayment = async (e) => {
    e.preventDefault();
    setPaySubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/payments/initiate`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ firstname: payForm.payName, email: payForm.payEmail, phone: payForm.payPhone, course_name: 'Game Development Course - Admission Fee', amount: 3000 })
      });
      if (res.ok) { const html = await res.text(); document.open(); document.write(html); document.close(); }
    } catch (err) { console.error(err); }
    setPaySubmitting(false);
  };

  const scrollToForm = () => document.getElementById('booking-form')?.scrollIntoView({ behavior: 'smooth' });

  const curriculum = [
    { month: 'Month 1', title: 'Game Design Fundamentals', topics: ['Game Design Principles & Theory', 'UI/UX for Games', 'Blender Basics & 3D Assets', 'Character & Environment Modeling'] },
    { month: 'Month 2', title: 'Game Engines', topics: ['Unity / Unreal Engine Setup', 'Physics & Collision Systems', 'Gameplay Mechanics & Systems', 'Scene Creation & Level Design'] },
    { month: 'Month 3', title: 'Advanced Game Dev', topics: ['Character Animation & Rigging', 'Combat & Interaction Systems', 'Multiplayer Basics & Networking', 'Sound Design & Visual Effects'] },
    { month: 'Month 4', title: 'Ship Your Game', topics: ['Game Publishing & Stores', 'Portfolio & Resume Building', 'Monetization Strategies', 'Final Game Project Submission'] }
  ];

  const careers = ['Game Developer', 'Level Designer', '3D Game Artist', 'Environment Artist', 'Unity/Unreal Creator', 'AR/VR Developer', 'Game Tester (QA)', 'Indie Studio Founder'];
  const tools = ['Unity', 'Unreal Engine', 'Blender', 'C# / Blueprints', 'Photoshop', 'Substance Painter', 'FMOD / Wwise', 'Git'];

  const faqs = [
    { q: 'Do I need coding experience?', a: 'No prior coding experience is needed. We teach C# and visual scripting from scratch.' },
    { q: 'Will I build a real game?', a: 'Yes! By the end of Month 4, you will have a complete, playable game for your portfolio.' },
    { q: 'Can I publish my game on Steam or Play Store?', a: 'Absolutely. We cover game publishing workflows for all major platforms.' },
    { q: 'Is this suitable for beginners?', a: 'Yes, the course is designed for complete beginners and gradually advances to intermediate skills.' },
    { q: 'What is the batch schedule?', a: 'Classes are held on weekends (Saturday & Sunday) with additional lab sessions during the week.' }
  ];

  const testimonials = [
    { name: 'Vikram Joshi', role: 'Indie Game Developer', quote: 'I published my first game on Steam within 6 months of completing this course. The mentorship was incredible.' },
    { name: 'Sneha Rao', role: 'Game Designer at Studio', quote: 'The Unreal Engine module was world-class. I landed my dream job at a gaming studio right after graduation.' },
    { name: 'Amit Kumar', role: 'Freelance 3D Artist', quote: 'The Blender training here is better than any YouTube tutorial. Real projects, real feedback, real growth.' }
  ];

  return (
    <div className="min-h-screen bg-white text-slate-900 overflow-x-hidden relative">
      <PageMeta
        title="Game Development Course | Job-Assured | AI School for All"
        description="Learn game development with Unity and industry tools. Job-assured program. Enroll at AI School for All."
        path="/game-development-course"
      />
      <div className="relative z-10">
      {/* STICKY MARQUEE */}
      <div className="bg-gradient-to-r from-emerald-600 via-cyan-600 to-emerald-700 py-3">
        <div className="flex items-center justify-center gap-4 px-4">
          <div className="hidden md:flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-green-200 animate-pulse" />
            <span className="text-sm font-bold text-white">Admissions Open for Admissions Open</span>
            <span className="text-slate-900/60">|</span>
            <span className="text-sm text-green-200 font-semibold">Limited Seats — 63% OFF!</span>
          </div>
          <Button onClick={scrollToForm} className="bg-white text-emerald-700 hover:bg-green-100 px-6 py-2 text-sm font-bold rounded-full">
            Reserve Your Seat <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </div>
      <SiteHeader />


      {/* HERO */}
      <section className="relative min-h-screen flex items-center pt-12 md:pt-16 overflow-hidden text-white">
        <div className="absolute inset-0">
          <img src="https://images.unsplash.com/photo-1766051666522-9cfa12675f5b?w=1920&h=1080&fit=crop&q=80" alt="Gaming Setup" className="w-full h-full object-cover" decoding="async" fetchPriority="high" loading="eager" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/95 via-black/80 to-emerald-900/40"></div>
          <div className="absolute inset-0 opacity-20" style={{ backgroundImage: 'linear-gradient(rgba(16,185,129,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(16,185,129,0.3) 1px, transparent 1px)', backgroundSize: '60px 60px' }}></div>
        </div>
        <div className="container mx-auto max-w-7xl px-6 relative z-10">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 bg-emerald-500/20 border border-emerald-500/30 rounded-full px-4 py-2 mb-6">
              <Gamepad2 className="w-4 h-4 text-emerald-400" />
              <span className="text-sm font-bold text-emerald-300">4-Month Industry Program</span>
            </div>
            <h1 className="text-5xl md:text-7xl font-black mb-6 leading-tight">
              Build Your Own Games &<br /><span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-cyan-300">Enter The Gaming Industry</span>
            </h1>
            <p className="text-xl text-gray-300 mb-8 leading-relaxed max-w-2xl">Learn game design, Unity/Unreal Engine, 3D art, character animation, and publish your own game — from scratch to Steam/Play Store.</p>
            <div className="flex flex-col sm:flex-row gap-4 mb-8">
              <Button onClick={scrollToForm} className="bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-600 hover:to-cyan-600 text-white px-8 py-6 text-lg font-bold rounded-xl">
                Enroll Now (63% OFF) <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <EnquireNowButton courseName="Game Development Course" size="md" label="Enquire Now" variant="outline" />
            </div>
            <div className="flex flex-wrap gap-4">
              {['Offline + Online Hybrid', 'Industry Ready Program', 'Limited Seats'].map(t => (
                <div key={t} className="flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-400" /><span className="text-sm text-gray-300">{t}</span>
                </div>
              ))}
            </div>
            </div>
            <div className="lg:col-span-5">
              <HeroPricingCard
                courseName="Game Development Course"
                oldPrice={95000}
                newPrice={35000}
                discountLabel="63% OFF"
                badge="4-Month Industry Program"
                batchDate="New Batches Every Month"
                theme="emerald"
                whatsappMessage="Hi, I want to know about the Game Development Course"
              />
            </div>
          </div>
        </div>
      </section>

      {/* PREMIUM TRAINER PROFILE - 3rd Section */}
      <section className="py-24 px-6 bg-gradient-to-br from-black via-emerald-950/40 to-cyan-950/30 relative overflow-hidden">
        <div className="absolute top-1/4 left-0 w-[500px] h-[500px] rounded-full bg-emerald-500/15 blur-[140px]"></div>
        <div className="absolute bottom-1/4 right-0 w-[400px] h-[400px] rounded-full bg-cyan-500/15 blur-[120px]"></div>
        <div className="absolute inset-0 opacity-[0.05]" style={{ backgroundImage: 'linear-gradient(rgba(16,185,129,0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(16,185,129,0.4) 1px, transparent 1px)', backgroundSize: '60px 60px' }}></div>

        <div className="container mx-auto max-w-7xl relative">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-emerald-500/20 border border-emerald-500/40 rounded-full px-5 py-2 mb-5">
              <Award className="w-4 h-4 text-emerald-400" />
              <span className="text-xs font-bold text-emerald-300 uppercase tracking-widest">Meet Your Industry Mentor</span>
            </div>
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black mb-4 leading-tight">
              Learn From a <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 via-cyan-300 to-teal-300">Real Game Developer</span>
            </h2>
            <p className="text-lg text-gray-400 max-w-2xl mx-auto">Build games with a working professional — not just classroom theory.</p>
          </div>

          <div className="grid lg:grid-cols-5 gap-8 lg:gap-12 items-center">
            <div className="lg:col-span-2 relative">
              <div className="relative group">
                <div className="absolute -inset-2 bg-gradient-to-r from-emerald-500 via-cyan-400 to-teal-500 rounded-3xl blur-xl opacity-40 group-hover:opacity-70 transition-opacity"></div>
                <div className="relative rounded-3xl overflow-hidden ring-4 ring-emerald-500/40 shadow-2xl shadow-emerald-500/30">
                  <img
                    src="https://customer-assets.emergentagent.com/job_8d33ea52-c764-4e32-b8e2-de0e8040a6fa/artifacts/jyd37jw6_WhatsApp%20Image%202026-05-09%20at%2010.54.31.jpeg"
                    alt="Mr. Our Expert Trainers — Lead Trainer"
                    className="w-full aspect-[4/5] object-cover"
                    data-testid="trainer-saurabh-photo" decoding="async" loading="lazy" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-6">
                    <p className="text-xs font-bold text-emerald-300 uppercase tracking-widest mb-1">Lead Trainer</p>
                    <p className="text-3xl font-black text-white">Mr. Our Expert Trainers</p>
                  </div>
                </div>
                <div className="absolute -top-4 -right-4 bg-gradient-to-br from-emerald-500 to-cyan-600 text-white rounded-2xl px-4 py-3 shadow-2xl rotate-3 hidden md:block">
                  <p className="text-xs font-bold opacity-90">EXPERIENCE</p>
                  <p className="text-2xl font-black">15+ Yrs</p>
                </div>
                <div className="absolute -bottom-4 -left-4 bg-white text-black rounded-2xl px-4 py-3 shadow-2xl -rotate-3 hidden md:block">
                  <p className="text-xs font-bold opacity-70">STUDENTS</p>
                  <p className="text-2xl font-black">5,000+</p>
                </div>
              </div>
            </div>

            <div className="lg:col-span-3 space-y-6">
              <div>
                <h3 className="text-5xl md:text-6xl font-black text-white mb-3 leading-none">Mr. Our Expert Trainers</h3>
                <p className="text-xl text-emerald-300 font-semibold">Lead Trainer — Game Development, Unity, Unreal &amp; 3D Art</p>
              </div>

              <div className="grid grid-cols-3 gap-3">
                {[
                  { num: '15+', label: 'Years Experience' },
                  { num: '5,000+', label: 'Students Trained' },
                  { num: '50+', label: 'Games Shipped' }
                ].map((s, i) => (
                  <div key={i} className="bg-gradient-to-br from-emerald-500/10 to-cyan-500/5 border border-emerald-500/30 rounded-2xl p-4 text-center backdrop-blur-sm">
                    <p className="text-3xl font-black text-transparent bg-clip-text bg-gradient-to-br from-emerald-300 to-cyan-400">{s.num}</p>
                    <p className="text-xs text-gray-400 mt-1">{s.label}</p>
                  </div>
                ))}
              </div>

              <p className="text-base md:text-lg text-gray-300 leading-relaxed">
                A seasoned game developer and 3D artist with over <span className="font-bold text-emerald-300">15 years</span> in interactive media. Expert Trainer has guided students from absolute beginners to indie developers shipping games on Steam, Play Store, and itch.io. He blends technical mastery with creative storytelling.
              </p>

              <div className="grid sm:grid-cols-2 gap-x-4 gap-y-3 pt-2">
                {[
                  'Unity &amp; Unreal Engine expert',
                  'Blender 3D for characters &amp; environments',
                  'Mobile &amp; PC game publishing',
                  'Game design &amp; monetization strategy',
                  'Hands-on, project-based teaching',
                  'Mentorship until your first published game'
                ].map((t, i) => (
                  <div key={i} className="flex items-start gap-2 text-sm text-gray-300">
                    <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                    <span dangerouslySetInnerHTML={{ __html: t }} />
                  </div>
                ))}
              </div>

              <div className="border-l-4 border-emerald-500 pl-5 py-2 italic text-gray-300 bg-emerald-500/5 rounded-r-xl">
                "Every student ships a real game. That's the only way to truly learn — by building."
                <p className="not-italic text-emerald-400 font-bold text-sm mt-2">— Mr. Our Expert Trainers</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WHY GAMING */}
      <section className="py-24 px-6 bg-white relative">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-4xl sm:text-5xl font-black text-center mb-4">Gaming Industry <span className="text-emerald-600">Market Demand</span></h2>
          <p className="text-slate-600 text-center mb-16 max-w-2xl mx-auto">The $200B+ gaming industry is growing faster than ever — and it needs skilled creators.</p>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: <TrendingUp className="w-8 h-8" />, title: 'Billion-Dollar Industry', desc: 'Gaming is now larger than movies and music combined globally' },
              { icon: <Monitor className="w-8 h-8" />, title: 'Mobile Gaming Boom', desc: 'India is the world\'s largest mobile gaming market by downloads' },
              { icon: <Gamepad2 className="w-8 h-8" />, title: 'PC/Console Growth', desc: 'New consoles and PCs driving massive demand for game developers' },
              { icon: <Rocket className="w-8 h-8" />, title: 'Indie Creator Economy', desc: 'Solo developers earning millions through Steam and app stores' }
            ].map((item, i) => (
              <div key={i} className="bg-slate-50 border border-slate-200 rounded-2xl p-6 hover:border-emerald-400 transition-all">
                <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center text-emerald-600 mb-4">{item.icon}</div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">{item.title}</h3>
                <p className="text-sm text-slate-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CAREERS */}
      <section className="py-24 px-6 bg-white relative">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-4xl sm:text-5xl font-black text-center mb-16">Career <span className="text-emerald-600">Roles</span></h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="rounded-2xl overflow-hidden border border-slate-200">
              <img src="https://images.unsplash.com/photo-1636487658623-eb8f5ae60770?w=600&h=350&fit=crop&q=80" alt="Game Development" className="w-full h-48 object-cover" decoding="async" loading="lazy" />
              <div className="p-6 bg-slate-50">
                <h3 className="text-lg font-bold text-slate-900 mb-3">Development & Design</h3>
                <div className="flex flex-wrap gap-2">
                  {['Game Developer', 'Level Designer', '3D Game Artist', 'Environment Artist'].map((r, i) => (
                    <span key={i} className="bg-emerald-100 text-emerald-600 text-xs font-semibold px-3 py-1.5 rounded-full border border-emerald-200">{r}</span>
                  ))}
                </div>
              </div>
            </div>
            <div className="rounded-2xl overflow-hidden border border-slate-200">
              <img src="https://images.unsplash.com/photo-1558008258-7ff8888b42b0?w=600&h=350&fit=crop&q=80" alt="Gaming Studio" className="w-full h-48 object-cover" decoding="async" loading="lazy" />
              <div className="p-6 bg-slate-50">
                <h3 className="text-lg font-bold text-slate-900 mb-3">Engine & Emerging Tech</h3>
                <div className="flex flex-wrap gap-2">
                  {['Unity/Unreal Creator', 'AR/VR Developer', 'Game Tester (QA)', 'Indie Studio Founder'].map((r, i) => (
                    <span key={i} className="bg-emerald-100 text-emerald-600 text-xs font-semibold px-3 py-1.5 rounded-full border border-emerald-200">{r}</span>
                  ))}
                </div>
              </div>
            </div>
          </div>
          <div className="mt-12 text-center">
            <p className="text-slate-600">Average starting salary: <span className="text-emerald-600 font-bold text-2xl">&#8377;5-15 LPA</span> | Indie devs earn <span className="text-emerald-600 font-bold text-2xl">&#8377;50K-5L/month</span></p>
          </div>
        </div>
      </section>

      {/* CURRICULUM */}
      <section id="curriculum" className="py-24 px-6 bg-white relative">
        <div className="absolute inset-0 opacity-[0.06]" style={{ backgroundImage: 'linear-gradient(45deg, rgba(16,185,129,0.4) 25%, transparent 25%, transparent 75%, rgba(16,185,129,0.4) 75%)', backgroundSize: '40px 40px' }}></div>
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-4xl sm:text-5xl font-black text-center mb-4">4-Month <span className="text-emerald-600">Curriculum</span></h2>
          <p className="text-slate-600 text-center mb-16">From zero to game publisher in 4 months</p>
          <div className="grid md:grid-cols-2 gap-6">
            {curriculum.map((m, i) => (
              <div key={i} className="bg-slate-50 border border-slate-200 rounded-2xl p-6 hover:border-emerald-400 transition-all">
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-xs font-bold uppercase tracking-wider text-emerald-600 bg-emerald-100 px-3 py-1 rounded-full">{m.month}</span>
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-4">{m.title}</h3>
                <ul className="space-y-2">{m.topics.map((t, j) => (
                  <li key={j} className="flex items-start gap-2 text-sm text-slate-600"><CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />{t}</li>
                ))}</ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* STUDENT GAME SHOWCASE */}
      <section className="py-24 px-6 bg-white relative">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-4xl sm:text-5xl font-black text-center mb-16">Student <span className="text-emerald-600">Games</span></h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { title: '3D Platformer Game', img: 'https://images.unsplash.com/photo-1556438064-2d7646166914?w=600&h=400&fit=crop&q=80', desc: 'Full 3D platformer built in Unity with custom character animations.' },
              { title: 'Mobile Puzzle Game', img: 'https://images.unsplash.com/photo-1511512578047-dfb367046420?w=600&h=400&fit=crop&q=80', desc: 'Published on Play Store with 10K+ downloads — built during the course.' },
              { title: 'VR Experience Prototype', img: 'https://images.unsplash.com/photo-1622979135225-d2ba269cf1ac?w=600&h=400&fit=crop&q=80', desc: 'Immersive VR environment with physics interactions in Unreal Engine.' }
            ].map((p, i) => (
              <div key={i} className="bg-gradient-to-br from-emerald-50 to-cyan-50 border border-emerald-200 rounded-2xl overflow-hidden hover:border-emerald-400/40 transition-all group">
                <img src={p.img} alt={p.title} className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-500" decoding="async" loading="lazy" />
                <div className="p-6">
                  <h3 className="text-lg font-bold text-slate-900 mb-2">{p.title}</h3>
                  <p className="text-sm text-slate-600">{p.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* STUDENT WORK SHOWCASE — Horizontal Scrolling Gallery */}
      <section className="py-24 px-0 bg-gradient-to-br from-black via-emerald-950/30 to-cyan-950/30 relative overflow-hidden">
        <div className="absolute top-20 left-1/2 -translate-x-1/2 w-[80%] h-[2px] bg-gradient-to-r from-transparent via-emerald-500/40 to-transparent"></div>
        <div className="absolute top-1/2 right-0 w-[400px] h-[400px] bg-emerald-500/10 rounded-full blur-[140px]"></div>
        <div className="absolute top-1/2 left-0 w-[400px] h-[400px] bg-cyan-500/10 rounded-full blur-[140px]"></div>

        <div className="container mx-auto max-w-7xl px-6 relative mb-12">
          <div className="flex items-end justify-between flex-wrap gap-4">
            <div>
              <div className="inline-flex items-center gap-2 bg-emerald-500/20 border border-emerald-500/40 rounded-full px-4 py-1.5 mb-4">
                <Sparkles className="w-4 h-4 text-emerald-400" />
                <span className="text-xs font-bold text-emerald-300 uppercase tracking-widest">Games Built By Our Students</span>
              </div>
              <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black leading-tight">
                Student <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 via-cyan-300 to-teal-300">Showreel</span>
              </h2>
              <p className="text-lg text-gray-400 mt-3 max-w-xl">Indie games, 3D worlds & interactive experiences shipped by our students.</p>
            </div>
            <div className="text-sm text-emerald-300 font-semibold flex items-center gap-2">
              <span className="hidden md:inline">Scroll horizontally</span>
              <ArrowRight className="w-4 h-4 animate-pulse" />
            </div>
          </div>
        </div>

        <div data-testid="student-showcase-scroll" className="flex gap-5 overflow-x-auto snap-x snap-mandatory pb-8 px-6 md:px-12 scrollbar-hide" style={{ scrollbarWidth: 'none' }}>
          {[
            { title: 'Indie Platformer Game', tag: 'UNITY 2D', img: 'https://images.unsplash.com/photo-1556438064-2d7646166914?w=700&h=500&fit=crop&q=80', desc: 'Full platformer with pixel art, mechanics, audio & menu screens.' },
            { title: 'First-Person 3D Adventure', tag: 'UNREAL ENGINE', img: 'https://images.unsplash.com/photo-1542751371-adc38448a05e?w=700&h=500&fit=crop&q=80', desc: 'Open-world exploration prototype built with Unreal Engine.' },
            { title: '3D Character Modelling', tag: 'BLENDER', img: 'https://images.unsplash.com/photo-1622979135225-d2ba269cf1ac?w=700&h=500&fit=crop&q=80', desc: 'Stylized characters with full rigging & animation cycles.' },
            { title: 'Mobile Hyper-Casual', tag: 'PLAY STORE', img: 'https://images.unsplash.com/photo-1612287230202-1ff1d85d1bdf?w=700&h=500&fit=crop&q=80', desc: 'One-tap hyper-casual games designed for mobile monetization.' },
            { title: 'Environment Art', tag: '3D ENVIRONMENT', img: 'https://images.unsplash.com/photo-1593305841991-05c297ba4575?w=700&h=500&fit=crop&q=80', desc: 'Cyberpunk & fantasy environments built from scratch.' },
            { title: 'Game UI & VFX', tag: 'VISUAL DESIGN', img: 'https://images.unsplash.com/photo-1493711662062-fa541adb3fc8?w=700&h=500&fit=crop&q=80', desc: 'In-game UI, particle effects & combat VFX systems.' },
            { title: 'Steam-Ready Indie Demo', tag: 'PUBLISHED', img: 'https://images.unsplash.com/photo-1538481199705-c710c4e965fc?w=700&h=500&fit=crop&q=80', desc: 'Polished demos ready for Steam release & investor pitches.' }
          ].map((p, i) => (
            <div key={i} className="snap-start flex-shrink-0 w-[300px] md:w-[380px] group cursor-pointer">
              <div className="relative rounded-2xl overflow-hidden border border-emerald-500/20 hover:border-emerald-400/60 transition-all">
                <img src={p.img} alt={p.title} className="w-full h-[260px] md:h-[300px] object-cover group-hover:scale-110 transition-transform duration-700" decoding="async" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent"></div>
                <div className="absolute top-3 left-3">
                  <span className="bg-emerald-500/90 text-white text-[10px] font-black px-2.5 py-1 rounded-md tracking-widest backdrop-blur-sm">{p.tag}</span>
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-5">
                  <h3 className="text-xl font-black text-white mb-2">{p.title}</h3>
                  <p className="text-sm text-gray-300 line-clamp-2">{p.desc}</p>
                </div>
              </div>
            </div>
          ))}
          <div className="flex-shrink-0 w-6"></div>
        </div>

        <div className="text-center mt-6 text-sm text-gray-500">
          <span className="inline-flex items-center gap-2"><Sparkles className="w-3 h-3 text-emerald-400" /> Swipe / drag to explore <Sparkles className="w-3 h-3 text-emerald-400" /></span>
        </div>
      </section>

      <CertificatesSection theme="emerald" courseName="Mastering Game Development" />

      {/* WHY AI SCHOOL */}
      <section className="py-24 px-6 bg-white relative">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-4xl sm:text-5xl font-black text-center mb-16">Why <span className="text-emerald-600">AI School For All</span></h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: <Award />, title: "20+ Year Legacy", desc: 'Workshop Library\'s decades of training excellence.' },
              { icon: <Globe />, title: 'Global Ecosystem', desc: 'Students trained across India and internationally.' },
              { icon: <Target />, title: 'Industry Curriculum', desc: 'Designed with active game studios and professionals.' },
              { icon: <Rocket />, title: 'Real Projects', desc: 'Work on actual game prototypes and publish them.' },
              { icon: <Users />, title: 'Mentorship', desc: 'Personal mentorship from experienced game developers.' },
              { icon: <Box />, title: 'Portfolio Ready', desc: 'Graduate with a published game on your resume.' }
            ].map((item, i) => (
              <div key={i} className="flex items-start gap-4 bg-slate-50 border border-slate-200 rounded-xl p-5 hover:border-emerald-400 transition-all">
                <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center text-emerald-600 flex-shrink-0">{item.icon}</div>
                <div><h3 className="font-bold text-slate-900 mb-1">{item.title}</h3><p className="text-sm text-slate-600">{item.desc}</p></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-24 px-6 bg-white relative">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-4xl sm:text-5xl font-black text-center mb-16">What Students <span className="text-emerald-600">Say</span></h2>
          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((t, i) => (
              <div key={i} className="bg-slate-50 border border-slate-200 rounded-2xl p-6">
                <div className="flex gap-1 mb-4">{[...Array(5)].map((_, j) => <Star key={j} className="w-4 h-4 fill-emerald-400 text-emerald-600" />)}</div>
                <p className="text-slate-700 mb-4 italic">"{t.quote}"</p>
                <div><p className="font-bold text-slate-900">{t.name}</p><p className="text-sm text-emerald-600">{t.role}</p></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CERT + TOOLS */}
      <section className="py-24 px-6 bg-white relative">
        <div className="container mx-auto max-w-6xl">
          <div className="grid md:grid-cols-2 gap-12">
            <div>
              <h2 className="text-3xl font-black mb-6">Industry <span className="text-emerald-600">Certification</span></h2>
              <div className="bg-gradient-to-br from-emerald-50 to-cyan-50 border border-emerald-200 rounded-2xl p-8">
                <Award className="w-12 h-12 text-emerald-600 mb-4" />
                <h3 className="text-xl font-bold text-slate-900 mb-2">AI School For All Certificate</h3>
                <p className="text-slate-600 mb-4">Powered by Workshop Library — recognized by gaming studios and tech recruiters.</p>
                <ul className="space-y-2">
                  {['Industry-recognized credential', 'Published game in portfolio', 'Placement assistance provided', 'Internship at partner studios'].map((t, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-slate-700"><CheckCircle2 className="w-4 h-4 text-emerald-600" />{t}</li>
                  ))}
                </ul>
              </div>
            </div>
            <div>
              <h2 className="text-3xl font-black mb-6">Tools You'll <span className="text-emerald-600">Master</span></h2>
              <div className="grid grid-cols-2 gap-3">
                {tools.map((t, i) => (
                  <div key={i} className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-center hover:border-emerald-400 transition-all">
                    <p className="text-sm font-semibold text-slate-900">{t}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 px-6 bg-white relative">
        <div className="container mx-auto max-w-3xl">
          <h2 className="text-4xl sm:text-5xl font-black text-center mb-16">Frequently Asked <span className="text-emerald-600">Questions</span></h2>
          <div className="space-y-3">
            {faqs.map((faq, i) => (
              <div key={i} className="bg-slate-50 border border-slate-200 rounded-xl overflow-hidden">
                <button onClick={() => setOpenFaq(openFaq === i ? null : i)} className="w-full flex items-center justify-between p-5 text-left">
                  <span className="font-semibold text-slate-900 pr-4">{faq.q}</span>
                  <ChevronDown className={`w-5 h-5 text-emerald-600 flex-shrink-0 transition-transform ${openFaq === i ? 'rotate-180' : ''}`} />
                </button>
                {openFaq === i && <div className="px-5 pb-5 text-slate-600 text-sm">{faq.a}</div>}
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* DETAILED ENROLLMENT + ADMISSION PAYMENT */}
      <AdmissionEnrollment courseName="Game Development Course" theme="emerald" source="emerald-course-page" />
      <MobileCTABar courseName="Game Development" />

<SiteFooter accent="emerald" />

      {/* WHATSAPP */}
      <a href="https://wa.me/919891550666?text=Hi, I want to know about the Game Development Course" target="_blank" rel="noopener noreferrer"
        onClick={() => trackWhatsAppClick('game_dev_page')} className="fixed bottom-6 right-6 z-50 bg-green-500 hover:bg-green-600 text-white p-4 rounded-full shadow-2xl transition-all hover:scale-110" data-testid="whatsapp-float">
        <Phone className="w-6 h-6" />
      </a>

      {/* STICKY MOBILE CTA */}
      <div className="fixed bottom-0 left-0 right-0 z-40 md:hidden bg-white/95 backdrop-blur-md border-t border-emerald-200 shadow-2xl p-3">
        <Button onClick={scrollToForm} className="w-full bg-gradient-to-r from-emerald-500 to-cyan-500 text-white py-3 font-bold rounded-xl">
          Enroll Now — &#8377;35,000 (63% OFF)
        </Button>
      </div>
      </div>
    </div>
  );
};

export default GameDevelopmentCoursePage;
